import * as React from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '../../lib/utils';

export interface FloatingSaveBarProps {
  message?: React.ReactNode;
  saving?: boolean;
  saveDisabled?: boolean;
  discardLoading?: boolean;
  onSave?: () => void;
  onDiscard?: () => void;
  className?: string;
}

/**
 * The unsaved-changes bar, floating over the page.
 *
 * It used to replace the whole top bar, which took the navigation and the
 * account menu away exactly when someone might want to check something before
 * saving — and hid the page title they were editing. Floating it keeps the
 * page intact and still reads as modal, because it sits above the content and
 * follows the viewport.
 *
 * The testids and the visible button labels are unchanged: several e2e specs
 * save through this control.
 */
export function FloatingSaveBar({
  message,
  saving,
  saveDisabled,
  discardLoading,
  onSave,
  onDiscard,
  className,
}: FloatingSaveBarProps) {
  return (
    <div
      role="region"
      aria-label="Unsaved changes"
      className={cn(
        'pointer-events-none fixed inset-x-0 bottom-0 z-30 flex justify-center p-4',
        className,
      )}
    >
      <div className="pointer-events-auto flex items-center gap-3 rounded-xl bg-foreground py-2 pl-4 pr-2 text-background shadow-lg">
        <span aria-hidden className="h-1.5 w-1.5 shrink-0 rounded-full bg-amber-400" />
        {/* `?? ` rather than a default parameter: the shell contract allows a
            null message, which a default would not catch. */}
        <span className="text-sm font-medium">{message ?? 'Unsaved changes'}</span>
        <div className="flex items-center gap-1.5">
          {!discardLoading && (
            <button
              type="button"
              data-testid="product-discard-button"
              onClick={onDiscard}
              className="rounded-lg px-3 py-1.5 text-sm font-medium text-background/80 transition-colors hover:bg-background/10 hover:text-background"
            >
              Discard
            </button>
          )}
          <button
            type="button"
            data-testid="product-save-button"
            onClick={onSave}
            disabled={saving || saveDisabled}
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-3.5 py-1.5 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-60"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
